import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';

import { DefaultButton } from 'common/components';
import { CheckBox } from 'crm/_components';

import { getPartnershipLinkDistributions } from '../../api';
import { staticStyles } from '../SelectLinkDistributionModal/style';

const PAGE_SIZE_OPTIONS = [10, 20, 50];

const LinkDistributionSelector = ({ userId, resetKey, onSelectionChange, onError }) => {
  const [items, setItems] = useState([]);
  const [selectedCodes, setSelectedCodes] = useState([]);
  const [tags, setTags] = useState([]);
  const [draftTypeFilter, setDraftTypeFilter] = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  const [tagFilter, setTagFilter] = useState('');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(PAGE_SIZE_OPTIONS[0]);
  const [isLoading, setIsLoading] = useState(false);
  const [total, setTotal] = useState(0);
  const [loadedRequestKey, setLoadedRequestKey] = useState('');
  const requestKey = `${userId}:${page}:${pageSize}:${typeFilter}:${tagFilter}`;

  useEffect(() => {
    setSelectedCodes([]);
    setDraftTypeFilter('');
    setTypeFilter('');
    setTagFilter('');
    setPage(1);
  }, [resetKey]);

  useEffect(() => {
    let mounted = true;
    setIsLoading(true);
    const params = {
      page,
      page_size: pageSize,
      user_id: userId,
      ...(typeFilter ? { search: typeFilter } : {}),
      ...(tagFilter ? { tag: tagFilter } : {}),
    };

    setItems([]);
    getPartnershipLinkDistributions(params)
      .then(({ data }) => {
        if (!mounted) return;
        const payload = data || {};
        let results = [];
        if (Array.isArray(payload.results)) {
          results = payload.results;
        } else if (Array.isArray(payload)) {
          results = payload;
        }
        setItems(results);
        setTags(Array.isArray(payload.tags) ? payload.tags : []);
        setTotal(payload.count ?? results.length);
        setLoadedRequestKey(requestKey);
      })
      .catch(error => {
        if (!mounted) return;
        setItems([]);
        setTags([]);
        setTotal(0);
        setLoadedRequestKey(requestKey);
        onError(error);
      })
      .finally(() => {
        if (mounted) setIsLoading(false);
      });

    return () => {
      mounted = false;
    };
  }, [page, pageSize, requestKey, tagFilter, typeFilter, onError, userId]);

  const pageCount = Math.max(1, Math.ceil(total / pageSize));
  const visibleItems = loadedRequestKey === requestKey && !isLoading ? items : [];
  const selectableVisibleItems = visibleItems.filter(item => !item.isUnavailable);
  const allVisibleSelected =
    selectableVisibleItems.length > 0 && selectableVisibleItems.every(item => selectedCodes.includes(item.code));

  useEffect(() => {
    if (page > pageCount) setPage(pageCount);
  }, [page, pageCount]);

  const updateSelection = nextSelection => {
    setSelectedCodes(nextSelection);
    onSelectionChange(nextSelection);
  };

  const toggleSelected = item => {
    if (item.isUnavailable) return;
    const { code } = item;
    updateSelection(
      selectedCodes.includes(code) ? selectedCodes.filter(itemCode => itemCode !== code) : [...selectedCodes, code]
    );
  };

  const toggleAllVisible = () => {
    if (allVisibleSelected) {
      updateSelection(selectedCodes.filter(code => !selectableVisibleItems.some(item => item.code === code)));
    } else {
      updateSelection(Array.from(new Set([...selectedCodes, ...selectableVisibleItems.map(item => item.code)])));
    }
  };

  const applySearch = () => {
    setTypeFilter(draftTypeFilter.trim());
    setPage(1);
  };

  const clearFilters = () => {
    setDraftTypeFilter('');
    setTypeFilter('');
    setTagFilter('');
    setPage(1);
  };

  return (
    <div className="SelectLinkDistributionModal">
      <div className="SelectLinkDistributionModal__filters">
        <label>
          <span>Type</span>
          <input
            value={draftTypeFilter}
            onChange={event => setDraftTypeFilter(event.target.value)}
            placeholder="Type"
          />
        </label>
        <label>
          <span>Tag</span>
          <select
            className="SelectLinkDistributionModal__tag-filter"
            value={tagFilter}
            onChange={event => {
              setTagFilter(event.target.value);
              setPage(1);
            }}>
            <option value="" disabled hidden>
              Tag
            </option>
            {tags.map(tag => (
              <option key={tag} value={tag}>
                {tag}
              </option>
            ))}
          </select>
        </label>
        <DefaultButton type="button" textId="justSearch" onClick={applySearch} />
        <DefaultButton type="button" textId="justReset" onClick={clearFilters} />
      </div>

      <div className="SelectLinkDistributionModal__table-wrapper">
        <table>
          <thead>
            <tr>
              <th>
                <CheckBox
                  checked={allVisibleSelected}
                  disabled={!selectableVisibleItems.length}
                  onClick={toggleAllVisible}
                />
              </th>
              <th>No.</th>
              <th>Type</th>
              <th>Attribute</th>
              <th>FX</th>
              <th>METALS</th>
              <th>COMMODITIES</th>
              <th>Tag</th>
            </tr>
          </thead>
          <tbody>
            {isLoading && (
              <tr>
                <td colSpan="8" className="SelectLinkDistributionModal__empty">
                  Loading...
                </td>
              </tr>
            )}
            {!isLoading && !visibleItems.length && (
              <tr>
                <td colSpan="8" className="SelectLinkDistributionModal__empty">
                  No link distributions found.
                </td>
              </tr>
            )}
            {visibleItems.map((item, index) => (
              <tr className={item.isUnavailable ? 'SelectLinkDistributionModal__row--unavailable' : ''} key={item.id}>
                <td>
                  <CheckBox
                    checked={selectedCodes.includes(item.code)}
                    disabled={item.isUnavailable}
                    onClick={() => toggleSelected(item)}
                  />
                </td>
                <td>{(page - 1) * pageSize + index + 1}</td>
                <td>{item.ibType || '---'}</td>
                <td>
                  <span
                    className={`SelectLinkDistributionModal__attribute-badge ${
                      item.attribute === 'mark-up' ? 'SelectLinkDistributionModal__attribute-badge--markup' : ''
                    }`}>
                    {item.attribute === 'mark-up' ? 'Mark-up' : item.attribute || 'Basic'}
                  </span>
                </td>
                <td>{item.fxSetting ?? '---'}</td>
                <td>{item.metalsSetting ?? '---'}</td>
                <td>{item.commoditiesSetting ?? '---'}</td>
                <td>
                  <span className="SelectLinkDistributionModal__tag-badge">{item.tag || '---'}</span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="SelectLinkDistributionModal__pagination">
        <span>Total {total}</span>
        <select
          aria-label="Page size"
          value={pageSize}
          onChange={event => {
            setPageSize(Number(event.target.value));
            setPage(1);
          }}>
          {PAGE_SIZE_OPTIONS.map(size => (
            <option key={size} value={size}>
              {size}/page
            </option>
          ))}
        </select>
        <button type="button" disabled={page <= 1} onClick={() => setPage(current => current - 1)}>
          ‹
        </button>
        <strong>{page}</strong>
        <button type="button" disabled={page >= pageCount} onClick={() => setPage(current => current + 1)}>
          ›
        </button>
      </div>
      <style jsx global>
        {staticStyles}
      </style>
    </div>
  );
};

LinkDistributionSelector.propTypes = {
  userId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  resetKey: PropTypes.number.isRequired,
  onSelectionChange: PropTypes.func.isRequired,
  onError: PropTypes.func,
};

LinkDistributionSelector.defaultProps = {
  onError: () => {},
};

export default LinkDistributionSelector;
export { LinkDistributionSelector };
